/**
 * How big a request is, said the same way everywhere: the builder, the send
 * sheet, the agent's approval card, the client's welcome screen and the
 * portal (the backend's `client_portal.views.task_row` mirrors this).
 *
 * A "question" is what the client is numbered through: a top-level question,
 * or a repeatable group (addresses, jobs) as one. Time counts every question
 * inside a group once.
 */
import type {
  OutgoingIntakeAnswer,
  OutgoingIntakeFormNode
} from "@/api/types/outgoingIntake";

import { flattenIntakeStepNodes } from "./intakeStepFlattening";

/** Finding and uploading a document takes far longer than typing an answer. */
export const MINUTES_PER_UPLOAD = 1;
export const MINUTES_PER_ANSWER = 0.25;

export const minutesForQuestion = (
  node: Pick<OutgoingIntakeFormNode, "question_type"> | undefined | null
): number =>
  node?.question_type === "file" ? MINUTES_PER_UPLOAD : MINUTES_PER_ANSWER;

/** Whole minutes, never "0" while anything is left. */
export const roundMinutes = (minutes: number): number =>
  minutes > 0 ? Math.max(1, Math.ceil(minutes)) : 0;

export type IntakeEstimate = {
  questions: number;
  uploads: number;
  answered: number;
  /** Minutes for what's still open. */
  minutes: number;
};

const questionsOf = (
  nodes: OutgoingIntakeFormNode[],
  out: OutgoingIntakeFormNode[] = []
): OutgoingIntakeFormNode[] => {
  for (const node of nodes ?? []) {
    if (node.node_type === "question") out.push(node);
    if (node.children?.length) questionsOf(node.children, out);
  }
  return out;
};

const hasValue = (value: unknown): boolean =>
  value !== null &&
  value !== undefined &&
  value !== "" &&
  !(Array.isArray(value) && value.length === 0);

/** Ids of answered questions, and of the repeatable groups that have one. */
export const answeredIdsOf = (
  answers: ReadonlyArray<OutgoingIntakeAnswer> | null | undefined
): Set<string> => {
  const out = new Set<string>();
  for (const a of answers ?? []) {
    if (a?.question_id && (hasValue(a.value) || (a.files ?? []).length)) {
      out.add(String(a.question_id));
      if (a.repeatable_group_id) out.add(String(a.repeatable_group_id));
    }
  }
  return out;
};

/** The numbered items: top-level questions and repeatable groups. */
export const intakeSteps = (
  schema: OutgoingIntakeFormNode[]
): OutgoingIntakeFormNode[] =>
  flattenIntakeStepNodes(schema).filter(
    (n) => n.node_type === "question" || n.node_type === "repeatable_group"
  );

/**
 * `answeredIds` holds question ids with an answer; a group counts as answered
 * when any of its questions is (or its own id is in the set).
 */
export const intakeEstimate = (
  schema: OutgoingIntakeFormNode[],
  answeredIds: ReadonlySet<string> = new Set()
): IntakeEstimate => {
  const steps = intakeSteps(schema);
  const isAnswered = (step: OutgoingIntakeFormNode) =>
    answeredIds.has(String(step.node_id)) ||
    (step.node_type === "repeatable_group" &&
      questionsOf(step.children ?? []).some((q) =>
        answeredIds.has(String(q.node_id))
      ));
  const open = steps.filter((s) => !isAnswered(s));
  const openMinutes = open.reduce(
    (sum, step) =>
      sum +
      (step.node_type === "question"
        ? minutesForQuestion(step)
        : questionsOf(step.children ?? []).reduce(
            (m, q) => m + minutesForQuestion(q),
            0
          )),
    0
  );
  return {
    questions: steps.length,
    uploads: questionsOf(schema).filter((q) => q.question_type === "file")
      .length,
    answered: steps.length - open.length,
    minutes: roundMinutes(openMinutes)
  };
};

const plural = (n: number, one: string, many = `${one}s`) =>
  `${n} ${n === 1 ? one : many}`;

/** "Fri, Oct 23". */
export const formatDue = (date: Date): string =>
  date.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric"
  });

/**
 * The attorney's one-line summary: "2 of 102 answered · 26 uploads · about 55
 * min left · due Fri, Oct 23". Before anything is answered it reads "102
 * questions · 26 uploads · about 57 min".
 */
export const estimateLine = (
  estimate: IntakeEstimate,
  options: { due?: Date | null; progress?: boolean } = {}
): string => {
  const { questions, uploads, answered, minutes } = estimate;
  const showProgress = options.progress && answered > 0;
  return [
    showProgress
      ? `${answered} of ${questions} answered`
      : plural(questions, "question"),
    uploads ? plural(uploads, "upload") : null,
    minutes ? `about ${minutes} min${showProgress ? " left" : ""}` : null,
    options.due ? `due ${formatDue(options.due)}` : null
  ]
    .filter(Boolean)
    .join(" · ");
};
